import { useEffect, useRef, useState } from 'react';
import type { DigitalCodePoolDto, DigitalCodesAddedDto, DigitalFileDto, ProductDetailDto } from '@ecom/contracts';
import { Link } from 'react-router-dom';
import { FilterBar } from '@/components/filters';
import { FileIcon, KeyIcon, PencilIcon, TrashIcon, UploadIcon } from '@/components/icons';
import { LoadMore, Section, Segmented, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Field, IconButton, Input, Pill, TableSkeleton, Textarea } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { age, count, fileSize } from '@/lib/format';
import { useDigitalStore } from '@/stores/digital.store';

/**
 * What a digital product delivers (ADR-077): a download's files, or a code's
 * pools — one per variant. Both are kept with the product, and saved as they
 * are changed, because each is its own record.
 */
export function DigitalCard({ product }: { product: ProductDetailDto }) {
  const load = useDigitalStore((state) => state.load);
  const kind = product.kind === 'CODE' ? 'CODE' : 'DOWNLOAD';
  useEffect(() => {
    void load(product.id, kind);
  }, [product.id, kind, load]);
  return kind === 'DOWNLOAD' ? <FilesCard /> : <PoolsCard product={product} />;
}

function FilesCard() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { files, loading, saving, error, uploadFile, removeFile } = useDigitalStore();
  const picker = useRef<HTMLInputElement>(null);
  const [renaming, setRenaming] = useState<DigitalFileDto | null>(null);
  const [deleting, setDeleting] = useState<DigitalFileDto | null>(null);

  const upload = (
    <>
      <input
        ref={picker}
        type="file"
        className="hidden"
        accept=".pdf,.zip,.epub,.docx,.xlsx,.pptx,.mp3,.mp4,.jpg,.jpeg,.png,.webp"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void uploadFile(file);
        }}
      />
      <Button variant="secondary" size="sm" disabled={saving} onClick={() => picker.current?.click()}>
        <UploadIcon className="size-4" />
        {saving ? t('uploadingImage') : t('digitalUpload')}
      </Button>
    </>
  );

  return (
    <Section title={t('digitalFiles')} subtitle={t('digitalFilesHint')} action={files.length > 0 ? upload : undefined}>
      {error ? (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      ) : null}
      {loading && files.length === 0 ? (
        <TableSkeleton columns={3} />
      ) : files.length === 0 ? (
        <EmptyState title={t('digitalNoFiles')} body={t('digitalNoFilesHint')} icon={<FileIcon />} action={upload} />
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{t('digitalFileName')}</Th>
                <Th>{t('digitalFileSize')}</Th>
                <Th>{t('digitalFileAdded')}</Th>
                <Th align="end">
                  <span className="sr-only">{t('digitalActions')}</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <Tr key={file.id}>
                  <Td>
                    <span className="flex items-center gap-2">
                      <FileIcon className="size-4 shrink-0 text-ink-faint" />
                      <bdi className="truncate font-medium text-ink">{file.name}</bdi>
                    </span>
                  </Td>
                  <Td className="whitespace-nowrap text-ink-muted">{fileSize(file.size, locale)}</Td>
                  <Td className="whitespace-nowrap text-ink-muted">{age(file.createdAt, locale)}</Td>
                  <Td align="end">
                    <span className="inline-flex gap-1">
                      <IconButton label={t('digitalRename')} onClick={() => setRenaming(file)} className="size-8 border-0">
                        <PencilIcon className="size-4" />
                      </IconButton>
                      <IconButton label={t('delete')} onClick={() => setDeleting(file)} className="size-8 border-0 text-danger">
                        <TrashIcon className="size-4" />
                      </IconButton>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      {renaming ? <RenameSheet file={renaming} onDone={() => setRenaming(null)} /> : null}
      <ConfirmDialog
        open={deleting !== null}
        title={t('digitalDeleteFileTitle')}
        body={t('digitalDeleteFileBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (deleting) await removeFile(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </Section>
  );
}

/*
 * The sheets below sit inside the product editor's own form, so they hold no
 * form of their own — a nested one would submit the product too. Their buttons
 * act on click, as the variant sheet's do.
 */

function RenameSheet({ file, onDone }: { file: DigitalFileDto; onDone: () => void }) {
  const t = useT();
  const { saving, renameFile } = useDigitalStore();
  const [name, setName] = useState(file.name);
  const save = async () => {
    if (await renameFile(file.id, name)) onDone();
  };
  return (
    <Sheet
      open
      title={t('digitalRename')}
      subtitle={t('digitalRenameHint')}
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="button" onClick={() => void save()} disabled={saving || !name.trim()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <Field label={t('digitalFileName')} htmlFor="digital-file-name">
        <Input
          id="digital-file-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            // Enter saves the name, never the product around it.
            if (event.key === 'Enter') {
              event.preventDefault();
              void save();
            }
          }}
          maxLength={120}
        />
      </Field>
    </Sheet>
  );
}

function PoolsCard({ product }: { product: ProductDetailDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { pools, loading, error } = useDigitalStore();
  const [adding, setAdding] = useState<DigitalCodePoolDto | null>(null);
  const [viewing, setViewing] = useState<DigitalCodePoolDto | null>(null);
  const single = product.variants.length === 1;

  return (
    <Section title={t('digitalCodes')} subtitle={t('digitalCodesHint')}>
      {error ? (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      ) : null}
      {loading && pools.length === 0 ? (
        <TableSkeleton columns={4} />
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{single ? t('digitalPool') : t('variant')}</Th>
                <Th align="end">{t('digitalAvailable')}</Th>
                <Th align="end">{t('digitalDelivered')}</Th>
                <Th align="end">
                  <span className="sr-only">{t('digitalActions')}</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {pools.map((pool) => (
                <Tr key={pool.variantId}>
                  <Td>
                    <span className="block font-medium text-ink">{pool.label || product.name}</span>
                    <code dir="ltr" className="text-xs text-ink-faint">
                      {pool.sku}
                    </code>
                  </Td>
                  <Td align="end">
                    {pool.available === 0 ? <Pill tone="danger">{t('digitalEmpty')}</Pill> : <span data-numeric>{count(pool.available, locale)}</span>}
                  </Td>
                  <Td align="end" className="text-ink-muted" data-numeric>
                    {count(pool.delivered, locale)}
                  </Td>
                  <Td align="end">
                    <span className="inline-flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setViewing(pool)}>
                        {t('digitalViewCodes')}
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setAdding(pool)}>
                        <KeyIcon className="size-3.5" />
                        {t('digitalAddCodes')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
      {adding ? <AddCodesSheet pool={adding} onDone={() => setAdding(null)} /> : null}
      {viewing ? <CodesSheet pool={viewing} onDone={() => setViewing(null)} /> : null}
    </Section>
  );
}

function AddCodesSheet({ pool, onDone }: { pool: DigitalCodePoolDto; onDone: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { saving, error, addCodes, clearError } = useDigitalStore();
  const [text, setText] = useState('');
  const [result, setResult] = useState<DigitalCodesAddedDto | null>(null);
  const lines = text.split(/\r?\n/).filter((line) => line.trim()).length;
  const add = async () => {
    const added = await addCodes(pool.variantId, text);
    if (added) {
      setResult(added);
      setText('');
    }
  };

  useEffect(() => {
    clearError();
  }, [clearError]);

  return (
    <Sheet
      open
      size="lg"
      title={t('digitalAddCodes')}
      subtitle={t('digitalAddCodesHint')}
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {result ? t('close') : t('cancel')}
          </Button>
          <Button type="button" onClick={() => void add()} disabled={saving || lines === 0}>
            {saving ? t('saving') : t('digitalAddCount', { count: count(lines, locale) })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-muted">
          <code dir="ltr">{pool.sku}</code> · {t('digitalAvailableNow', { count: count(result?.available ?? pool.available, locale) })}
        </p>
        <Field label={t('digitalCodesLines')} htmlFor="digital-codes" hint={t('digitalCodesLinesHint')}>
          <Textarea
            id="digital-codes"
            dir="ltr"
            rows={12}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={'WKSP-7Q2M-91XA\nWKSP-3HC8-PL2D'}
            className="font-mono text-xs"
          />
        </Field>
        {result ? (
          <Alert tone="success">
            {t('digitalAddedResult', { added: count(result.added, locale), duplicates: count(result.duplicates, locale) })}
          </Alert>
        ) : null}
        {error ? <Alert>{error}</Alert> : null}
      </div>
    </Sheet>
  );
}

function CodesSheet({ pool, onDone }: { pool: DigitalCodePoolDto; onDone: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { codes, saving, error, openCodes, moreCodes, removeCode, closeCodes } = useDigitalStore();
  const [state, setState] = useState<'available' | 'delivered'>('available');
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    void openCodes(pool.variantId, state, search);
  }, [pool.variantId, state, search, openCodes]);
  useEffect(() => closeCodes, [closeCodes]);

  const items = codes?.items ?? [];
  return (
    <Sheet open size="lg" title={t('digitalCodesOf', { sku: pool.sku })} onClose={onDone}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            label={t('digitalCodes')}
            items={[
              { id: 'available', label: t('digitalAvailable') },
              { id: 'delivered', label: t('digitalDelivered') },
            ]}
            value={state}
            onChange={setState}
          />
          <div className="min-w-48 flex-1">
            <FilterBar search={search} onSearch={setSearch} placeholder={t('digitalSearchCodes')} total={codes?.total ?? undefined} shown={items.length} />
          </div>
        </div>
        {error ? <Alert>{error}</Alert> : null}
        {codes?.loading && items.length === 0 ? (
          <TableSkeleton columns={2} />
        ) : items.length === 0 ? (
          <EmptyState title={state === 'available' ? t('digitalNoAvailable') : t('digitalNoDelivered')} icon={<KeyIcon />} />
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-control border border-line">
            {items.map((code) => (
              <li key={code.id} className="flex items-center gap-3 px-3 py-2">
                <code dir="ltr" className="min-w-0 flex-1 truncate font-mono text-sm text-ink">
                  {code.code}
                </code>
                {code.order ? (
                  <Link to={`/orders/${code.order.id}`} className="text-xs text-brand-600 hover:underline">
                    <bdi dir="ltr">{code.order.number}</bdi>
                    {code.assignedAt ? ` · ${age(code.assignedAt, locale)}` : ''}
                  </Link>
                ) : (
                  <IconButton label={t('delete')} onClick={() => setDeleting(code.id)} className="size-8 border-0 text-danger">
                    <TrashIcon className="size-4" />
                  </IconButton>
                )}
              </li>
            ))}
          </ul>
        )}
        <LoadMore visible={Boolean(codes?.nextCursor)} loading={Boolean(codes?.loading)} onClick={() => void moreCodes()} />
      </div>
      <ConfirmDialog
        open={deleting !== null}
        title={t('digitalDeleteCodeTitle')}
        body={t('digitalDeleteCodeBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (deleting) await removeCode(deleting);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </Sheet>
  );
}
